/**
 * Reduced-motion + count-up hooks for the v2 landing redesign.
 *
 * REDUCED-MOTION RULE: framer respects prefers-reduced-motion for the
 * whileInView/animate built-ins, but every repeat:Infinity loop and every
 * count-up / gauge must ALSO be gated behind usePrefersReducedMotion() and
 * render its end-state statically. These hooks make that the easy default.
 */
import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * True when the user prefers reduced motion (OS-level). Thin, typed wrapper
 * over framer's `useReducedMotion` that always returns a boolean (never null)
 * so callers can use it directly in conditionals.
 *
 *   const reduced = usePrefersReducedMotion();
 *   <motion.span animate={reduced ? undefined : { x: [0, 10, 0] }} />
 */
export function usePrefersReducedMotion(): boolean {
  return useReducedMotion() ?? false;
}

export interface CountUpOptions {
  /** Tween duration in seconds. Default 1.2. */
  duration?: number;
  /** Decimal places to render. Default 0. */
  decimals?: number;
  /** Start the tween immediately on mount rather than waiting for `start`. */
  startOnMount?: boolean;
  /** Locale for thousands grouping. Default 'en-US' (tabular Latin figures). */
  locale?: string;
}

/**
 * rAF-driven count-up over a numeric target.
 *
 * Returns `[displayString, begin]`:
 *  - `displayString` — the formatted current value (grouped, tabular).
 *  - `begin()` — call when the element enters the viewport to start the tween
 *    (no-op if already running/finished). Ignored when `startOnMount` is set.
 *
 * Reduced-motion: returns the final target instantly, `begin` is a no-op.
 *
 *   const [value, begin] = useCountUp(94, { duration: 1.2 });
 *   <motion.span onViewportEnter={begin} className="num">{value}%</motion.span>
 */
export function useCountUp(
  target: number,
  { duration = 1.2, decimals = 0, startOnMount = false, locale = 'en-US' }: CountUpOptions = {},
): [string, () => void] {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState<number>(reduced || startOnMount ? target : 0);
  const startedRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const format = (n: number) =>
    n.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const begin = () => {
    if (reduced || startedRef.current) return;
    startedRef.current = true;
    const from = 0;
    const startTime = performance.now();
    const durMs = Math.max(duration, 0.01) * 1000;
    // easeOutCubic — settles gently, matching the reveal vocabulary.
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const tick = (now: number) => {
      const t = Math.min((now - startTime) / durMs, 1);
      setValue(from + (target - from) * ease(t));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (startOnMount) begin();
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the static value in sync if target changes while reduced/finished.
  useEffect(() => {
    if (reduced) setValue(target);
  }, [reduced, target]);

  return [format(value), begin];
}
